import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { EmptyState, ErrorState, LoadingState, Notice } from '@/components/ui/Feedback'
import type { AdminFetch } from './AdminCustomers'
import { categoryLabels, channelLabels, type SupportCategory, type SupportChannel } from './adminSupportText'

type Policy = { category: SupportCategory; channel: SupportChannel; autoSend: boolean; drafts: number; approvedUnchanged: number; approvedEdited: number; discarded: number }
type Usage = { budgetCents: number; todayCents: number; enabled: boolean; model: string; days: { day: string; runs: number; costCents: number; inputTokens: number; outputTokens: number }[] }
type Sync = { startedAt: string; finishedAt?: string | null; pages?: number | null; version?: string | null; status: 'RUNNING' | 'OK' | 'FAILED'; error?: string | null }
type Knowledge = { lastSync?: Sync | null; pages: number; chunks: number; enabled: boolean; release: string }

const when = new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short' })
const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

/** The share of judged drafts a person sent unchanged: what earns a category its automatic sending. */
function approvalRate(policy: Policy) {
  const judged = policy.approvedUnchanged + policy.approvedEdited + policy.discarded
  return judged === 0 ? null : Math.round((policy.approvedUnchanged / judged) * 100)
}

/**
 * How the assistant is let loose: per category and channel, whether its draft goes out without a person, beside
 * the measure that justifies it; what it spent today against its budget; and the manual it answers from.
 */
export function AdminSupportPolicies({ adminFetch, describeError }: { adminFetch: AdminFetch; describeError: (cause: unknown, fallback: string) => string }) {
  const [policies, setPolicies] = useState<Policy[]>([])
  const [usage, setUsage] = useState<Usage | null>(null)
  const [knowledge, setKnowledge] = useState<Knowledge | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const lastLoad = useRef(0)

  const load = useCallback(async () => {
    const ticket = ++lastLoad.current
    setLoading(true); setError('')
    try {
      const [rules, spend, manual] = await Promise.all([adminFetch<Policy[]>('/admin/support/policies'), adminFetch<Usage>('/admin/support/usage?days=30'), adminFetch<Knowledge>('/admin/support/kb')])
      if (ticket !== lastLoad.current) return
      setPolicies(rules); setUsage(spend); setKnowledge(manual)
    } catch (cause) {
      if (ticket === lastLoad.current) setError(describeError(cause, 'No pudimos leer la configuración del asistente.'))
    } finally {
      if (ticket === lastLoad.current) setLoading(false)
    }
  }, [adminFetch, describeError])

  useEffect(() => { void load() }, [load])

  async function toggle(policy: Policy) {
    const key = `${policy.category}/${policy.channel}`
    setBusy(key); setNotice(''); setError('')
    try {
      setPolicies(await adminFetch<Policy[]>('/admin/support/policies', { method: 'PUT', body: { category: policy.category, channel: policy.channel, autoSend: !policy.autoSend } }))
      setNotice(policy.autoSend ? `${categoryLabels[policy.category]} por ${channelLabels[policy.channel].toLowerCase()} vuelve a esperar tu aprobación.`
        : `${categoryLabels[policy.category]} por ${channelLabels[policy.channel].toLowerCase()} sale sin aprobación desde ahora.`)
    } catch (cause) { setError(describeError(cause, 'No pudimos cambiar la política.')) } finally { setBusy(null) }
  }

  async function sync() {
    setBusy('sync'); setNotice(''); setError('')
    try {
      const updated = await adminFetch<Knowledge>('/admin/support/kb/sync', { method: 'POST' })
      setKnowledge(updated)
      setNotice(updated.lastSync?.status === 'OK' ? `Manual sincronizado: ${updated.pages} páginas.` : `La sincronización falló: ${updated.lastSync?.error ?? 'sin detalle'}.`)
    } catch (cause) { setError(describeError(cause, 'No pudimos sincronizar el manual.')) } finally { setBusy(null) }
  }

  const channels = (Object.keys(channelLabels) as SupportChannel[]).filter((channel) => channel !== 'VOICE')
  const categories = Object.keys(categoryLabels) as SupportCategory[]
  const spent = usage ? Math.min(100, usage.budgetCents > 0 ? Math.round((usage.todayCents / usage.budgetCents) * 100) : 100) : 0

  return <section className="admin-customers admin-support-policies" aria-labelledby="admin-support-policies-heading">
    <div className="admin-section-heading"><div><h2 id="admin-support-policies-heading">Asistente de soporte</h2><p>Qué categorías salen sin tu aprobación, cuánto gasta el asistente al día y de qué manual responde.</p></div></div>
    {notice && <Notice tone="success">{notice}</Notice>}
    {error && (policies.length > 0 || usage) && <Notice>{error}</Notice>}
    {loading && !usage ? <LoadingState label="Leyendo la configuración del asistente…" />
      : error && !usage ? <ErrorState title={error} retry={() => void load()} />
      : <>
        {usage && <section className="admin-customer-block" aria-label="Gasto del asistente">
          <h4>Gasto de hoy</h4>
          {!usage.enabled && <Notice tone="info">El asistente está apagado: no hay clave de Anthropic configurada o el trabajador está detenido. Cada conversación la responde una persona.</Notice>}
          <p>{usd.format(usage.todayCents / 100)} de {usd.format(usage.budgetCents / 100)} al día con {usage.model}. Al llegar al tope, el asistente deja de redactar hasta mañana.</p>
          <div className="admin-support-budget" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={spent} aria-label="Presupuesto del día usado"><span style={{ width: `${spent}%` }} /></div>
          {usage.days.length > 0 && <p className="field-hint">Últimos días: {usage.days.slice(0, 7).map((day) => `${day.day.slice(5)} ${usd.format(day.costCents / 100)} (${day.runs})`).join(' · ')}</p>}
        </section>}
        <section className="admin-customer-block" aria-label="Envío automático">
          <h4>Envío sin aprobación</h4>
          <p className="field-hint">Cada celda dice cuántos borradores aprobaste sin cambios de los que juzgaste. Activa una categoría cuando esa tasa te convenza; una conversación que pide una persona nunca sale sola.</p>
          {policies.length === 0 ? <EmptyState title="Sin políticas" description="Cuando el asistente redacte borradores, aquí decides cuáles salen solos." />
            : <div className="price-table-wrap admin-support-policies-table"><table>
              <thead><tr><th>Categoría</th>{channels.map((channel) => <th key={channel}>{channelLabels[channel]}</th>)}</tr></thead>
              <tbody>{categories.map((category) => <tr key={category}><td>{categoryLabels[category]}</td>{channels.map((channel) => {
                const policy = policies.find((item) => item.category === category && item.channel === channel)
                if (!policy) return <td key={channel}>—</td>
                const rate = approvalRate(policy)
                const key = `${category}/${channel}`
                return <td key={channel}>
                  <Button emphasis="outline" intent="neutral" aria-pressed={policy.autoSend} busy={busy === key} onClick={() => void toggle(policy)} aria-label={`${categoryLabels[category]} por ${channelLabels[channel]}: ${policy.autoSend ? 'sale sin aprobación' : 'espera aprobación'}`}>{policy.autoSend ? 'Sale sola' : 'Con aprobación'}</Button>
                  <span className="admin-customers-sub">{rate === null ? `${policy.drafts} borradores, ninguno juzgado` : `${rate} % sin cambios de ${policy.approvedUnchanged + policy.approvedEdited + policy.discarded}`}</span>
                </td>
              })}</tr>)}</tbody>
            </table></div>}
        </section>
        {knowledge && <section className="admin-customer-block" aria-label="Manual de usuario">
          <h4>Manual de usuario</h4>
          <p>{knowledge.pages} páginas y {knowledge.chunks} secciones, grupos «{knowledge.release === 'all' ? 'todos' : 'revisados'}».
            {knowledge.lastSync ? ` Última sincronización ${when.format(new Date(knowledge.lastSync.startedAt))}: ${knowledge.lastSync.status === 'OK' ? 'correcta' : knowledge.lastSync.status === 'RUNNING' ? 'en curso' : `falló (${knowledge.lastSync.error ?? 'sin detalle'})`}.` : ' Aún no se ha sincronizado.'}
            {!knowledge.enabled && ' La sincronización nocturna está apagada o bspos-api no está configurado.'}</p>
          <div className="admin-support-actions"><Button emphasis="outline" intent="neutral" busy={busy === 'sync'} onClick={() => void sync()}>Sincronizar manual</Button></div>
        </section>}
      </>}
  </section>
}
